import axios from "axios";
import { getCookie, getJwtExpirationDate, removeCookie, setCookie } from "../lib/cookies";

class WlTokenService {
  constructor() {
    this.ACCESS_TOKEN_KEY = "wlt";
    this.REFRESH_TOKEN_KEY = "wlrt";
    this.ACCESS_EXPIRATION_KEY = "wlt_exp";
    this.REFRESH_EXPIRATION_KEY = "wlrt_exp";
    this.REFRESH_BUFFER_MS = 60 * 1000;
  }

  getAccessToken() {
    return getCookie(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken() {
    return getCookie(this.REFRESH_TOKEN_KEY);
  }

  getAccessExpirationDate() {
    const fromCookie = getCookie(this.ACCESS_EXPIRATION_KEY);
    if (fromCookie) {
      const parsed = new Date(fromCookie);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    return getJwtExpirationDate(this.getAccessToken());
  }

  isAccessTokenNearExpiration() {
    const expirationDate = this.getAccessExpirationDate();
    if (!expirationDate) {
      return false;
    }

    return expirationDate.getTime() - Date.now() <= this.REFRESH_BUFFER_MS;
  }

  parseExpirationDate(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value > 1e12 ? new Date(value) : new Date(value * 1000);
    }

    if (typeof value === "string") {
      const numeric = Number(value);
      if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
        return numeric > 1e12 ? new Date(numeric) : new Date(numeric * 1000);
      }
    }

    return null;
  }

  saveTokens(payload, options = {}) {
    if (!payload && !options?.accessToken && !options?.refreshToken) {
      return;
    }

    const rootData = payload && typeof payload === "object" ? payload : {};
    const nestedData =
      rootData?.data && typeof rootData.data === "object" ? rootData.data : {};

    const accessToken = options.accessToken || nestedData.token || rootData.token;
    const refreshToken =
      options.refreshToken ||
      nestedData.refresh_token ||
      rootData.refresh_token ||
      rootData.wlrt;

    let accessExpiration = this.parseExpirationDate(
      nestedData.expires_in ?? rootData.expires_in ?? rootData.expires_at
    );

    let refreshExpiration = this.parseExpirationDate(
      nestedData.refresh_expires_in ?? rootData.refresh_expires_in
    );

    if (!accessExpiration && accessToken) {
      accessExpiration = getJwtExpirationDate(accessToken);
    }

    if (!refreshExpiration && refreshToken) {
      refreshExpiration = getJwtExpirationDate(refreshToken);
    }

    if (accessToken) {
      setCookie(
        this.ACCESS_TOKEN_KEY,
        accessToken,
        accessExpiration ? { expiresAt: accessExpiration } : {}
      );

      if (accessExpiration) {
        setCookie(this.ACCESS_EXPIRATION_KEY, accessExpiration.toISOString(), {
          expiresAt: accessExpiration,
        });
      }
    }

    if (refreshToken) {
      setCookie(
        this.REFRESH_TOKEN_KEY,
        refreshToken,
        refreshExpiration ? { expiresAt: refreshExpiration } : {}
      );

      if (refreshExpiration) {
        setCookie(this.REFRESH_EXPIRATION_KEY, refreshExpiration.toISOString(), {
          expiresAt: refreshExpiration,
        });
      }
    }
  }

  clearTokens() {
    removeCookie(this.ACCESS_TOKEN_KEY);
    removeCookie(this.REFRESH_TOKEN_KEY);
    removeCookie(this.ACCESS_EXPIRATION_KEY);
    removeCookie(this.REFRESH_EXPIRATION_KEY);
  }

  getChannelFromToken(token) {
    let channel = process.env.REACT_APP_DEFAULT_CHANNEL;

    if (!token) {
      return channel;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      channel = payload?.channel ?? channel;
    } catch (error) {
      console.warn("Falha ao decodificar token para obter channel:", error);
    }

    return channel;
  }

  getChannel(accessToken = this.getAccessToken(), refreshToken = this.getRefreshToken()) {
    if (accessToken) {
      return this.getChannelFromToken(accessToken);
    }

    if (refreshToken) {
      return this.getChannelFromToken(refreshToken);
    }

    return process.env.REACT_APP_DEFAULT_CHANNEL;
  }

  async refreshToken() {
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new Error("Não foi possível renovar sessão: refresh token ausente");
    }

    const API_URL_CREDENCIAIS = `${process.env.REACT_APP_API_AUTHORIZATION}/v1`;
    const channel = this.getChannel(token, refreshToken);

    const { data } = await axios.post(`${API_URL_CREDENCIAIS}/jwt/refresh-channel`, {
      channel,
      refresh_token: refreshToken,
    });

    if (!data?.success || !data?.data?.token) {
      throw new Error(data?.message || "Falha ao renovar token de sessão");
    }

    this.saveTokens(data);
    return data.data.token;
  }
}

export default new WlTokenService();
